import { z } from 'zod'

/**
 * Contrapartes e caracterização de regime (arquitetura §8) — F4.
 * O regime do fornecedor decide quanto da compra vira crédito; o do cliente
 * decide se a venda transfere crédito. É variável de primeira ordem, não cadastro.
 */

/** Regime que o emitente declara no documento (CRT / opSimpNac / OptanteSimplesNacional). */
export const REGIMES_EMITENTE = ['SIMPLES', 'SIMPLES_EXCESSO', 'NORMAL', 'MEI'] as const
export type RegimeEmitente = (typeof REGIMES_EMITENTE)[number]

export const REGIMES_CONTRAPARTE = [
  'REGULAR', // Lucro Real ou Presumido — crédito integral
  'SN_PURO', // Simples integral — crédito limitado ao embutido no DAS
  'SN_HIBRIDO', // Simples com IBS/CBS por fora — crédito integral
  'MEI', // crédito limitado
  'PF_NAO_CONTRIBUINTE', // sem crédito
  'PRODUTOR_RURAL_NAO_CONTRIBUINTE', // regime próprio; crédito presumido quando houver
  'IMUNE_ISENTO', // sem crédito
  'DESCONHECIDO',
] as const
export type RegimeContraparte = (typeof REGIMES_CONTRAPARTE)[number]

export const ROTULO_REGIME_CONTRAPARTE: Record<RegimeContraparte, string> = {
  REGULAR: 'Regime regular (Real/Presumido)',
  SN_PURO: 'Simples — puro',
  SN_HIBRIDO: 'Simples — híbrido (IBS/CBS por fora)',
  MEI: 'MEI',
  PF_NAO_CONTRIBUINTE: 'Pessoa física',
  PRODUTOR_RURAL_NAO_CONTRIBUINTE: 'Produtor rural não contribuinte',
  IMUNE_ISENTO: 'Imune ou isento',
  DESCONHECIDO: 'Desconhecido',
}

/** Como o regime do fornecedor repercute no crédito da compra (§10.4) — sem valor de imposto aqui. */
export const CREDITO_POR_REGIME: Record<RegimeContraparte, 'INTEGRAL' | 'LIMITADO' | 'NENHUM' | 'INDEFINIDO'> = {
  REGULAR: 'INTEGRAL',
  SN_HIBRIDO: 'INTEGRAL',
  SN_PURO: 'LIMITADO',
  MEI: 'LIMITADO',
  PRODUTOR_RURAL_NAO_CONTRIBUINTE: 'LIMITADO',
  PF_NAO_CONTRIBUINTE: 'NENHUM',
  IMUNE_ISENTO: 'NENHUM',
  DESCONHECIDO: 'INDEFINIDO',
}

/**
 * O que as notas emitidas pela contraparte dizem do regime dela (CRT da NF-e, opSimpNac da NFS-e).
 * É sugestão, não confirmação: serve para já chegar respondido na tela e na pergunta ao cliente.
 * Simples não diz se ela optou por destacar o IBS/CBS (híbrido) — isso só ela sabe, e é o que se pergunta.
 */
export interface SugestaoRegime {
  /** o que a nota dela indica */
  indica: 'SIMPLES' | 'REGULAR' | 'MEI' | null
  /** já no formato do cadastro, pronto para o formulário (Simples cai no padrão do escritório) */
  regime: RegimeContraparte | null
  motivo: string
  /** Simples: falta saber se é puro ou híbrido — a pergunta que vale a pena fazer */
  perguntarPuroOuHibrido: boolean
}

export function sugestaoDeRegime(
  c: { tipoPessoa: 'CNPJ' | 'CPF' | 'ESTRANGEIRO'; regimeDeclarado: string | null },
  padraoSn: 'SN_PURO' | 'SN_HIBRIDO' = 'SN_PURO',
): SugestaoRegime {
  const nada = { indica: null, regime: null, perguntarPuroOuHibrido: false } as const
  if (c.tipoPessoa !== 'CNPJ') return { ...nada, motivo: 'pessoa física ou contraparte no exterior: não tem regime a sugerir' }
  switch (c.regimeDeclarado) {
    case 'SIMPLES':
    case 'SIMPLES_EXCESSO':
      return {
        indica: 'SIMPLES',
        regime: padraoSn,
        motivo: 'as notas que ela emite saem pelo Simples Nacional',
        perguntarPuroOuHibrido: true,
      }
    case 'MEI':
      return { indica: 'MEI', regime: 'MEI', motivo: 'as notas que ela emite saem como MEI', perguntarPuroOuHibrido: false }
    case 'NORMAL':
      return { indica: 'REGULAR', regime: 'REGULAR', motivo: 'as notas que ela emite saem pelo regime normal (Lucro Real ou Presumido)', perguntarPuroOuHibrido: false }
    default:
      return { ...nada, motivo: 'ela não emitiu nota para a empresa, ou a nota não diz o regime' }
  }
}

export const CONFIRMACOES = ['PRESUMIDO', 'CONFIRMADO'] as const
export type Confirmacao = (typeof CONFIRMACOES)[number]

/** CADASTRO_PROCESSOS: a contraparte é empresa da carteira do escritório e o regime vem do cadastro dela. */
export const FONTES_REGIME = ['CADASTRO_PROCESSOS', 'DOCUMENTO', 'CLIENTE', 'ESCRITORIO', 'PADRAO_TENANT'] as const
export type FonteRegime = (typeof FONTES_REGIME)[number]

export const ROTULO_FONTE_REGIME: Record<FonteRegime, string> = {
  CADASTRO_PROCESSOS: 'cadastro no Processos',
  DOCUMENTO: 'declarado nas notas',
  CLIENTE: 'informado pelo cliente',
  ESCRITORIO: 'definido pelo escritório',
  PADRAO_TENANT: 'padrão do escritório',
}

/** Destinação da venda (§8.3), na ordem de precedência. */
export const DESTINACOES_VENDA = ['B2C', 'B2B_COM_CREDITO', 'B2B_SEM_CREDITO', 'B2B_CONSUMO_FINAL', 'B2B_INDETERMINADO'] as const
export type DestinacaoVenda = (typeof DESTINACOES_VENDA)[number]

export const ROTULO_DESTINACAO_VENDA: Record<DestinacaoVenda, string> = {
  B2C: 'Consumidor final (B2C)',
  B2B_COM_CREDITO: 'Empresas que se creditam',
  B2B_SEM_CREDITO: 'Empresas sem crédito (Simples puro, MEI, imune)',
  B2B_CONSUMO_FINAL: 'Empresa de regime indefinido, como consumidora final',
  B2B_INDETERMINADO: 'Empresa de regime indefinido',
}

export const PAPEIS_CONTRAPARTE = ['FORNECEDOR', 'CLIENTE'] as const
export type PapelContraparte = (typeof PAPEIS_CONTRAPARTE)[number]

export const FiltroContraparte = z.object({
  papel: z.enum(PAPEIS_CONTRAPARTE),
  regime: z.enum(REGIMES_CONTRAPARTE).optional(),
  confirmacao: z.enum(CONFIRMACOES).optional(),
  busca: z.string().trim().max(100).optional(),
  pagina: z.number().int().min(1).default(1),
  porPagina: z.number().int().min(10).max(200).default(50),
})
export type FiltroContraparte = z.infer<typeof FiltroContraparte>

// ─── Pendências (§4.4) ───────────────────────────────────────────────────────

export const TIPOS_PENDENCIA = ['REGIME_CONTRAPARTE', 'DESCRICAO_PRODUTO', 'DESTINACAO_PRODUTO', 'CONFIRMA_MIX', 'PREMISSA_NEGOCIO'] as const
export type TipoPendencia = (typeof TIPOS_PENDENCIA)[number]

export const STATUS_PENDENCIA = ['ABERTA', 'RESPONDIDA', 'HOMOLOGADA', 'DEVOLVIDA'] as const
export type StatusPendencia = (typeof STATUS_PENDENCIA)[number]

export const ROTULO_STATUS_PENDENCIA: Record<StatusPendencia, string> = {
  ABERTA: 'Aguardando o cliente',
  RESPONDIDA: 'Respondida — homologar',
  HOMOLOGADA: 'Homologada',
  DEVOLVIDA: 'Devolvida ao cliente',
}

/** Resposta do cliente sobre regime: ele diz o que sabe; o escritório homologa. */
export const RespostaRegime = z.object({
  regime: z.enum(['SN_PURO', 'SN_HIBRIDO', 'REGULAR', 'MEI', 'NAO_SEI']),
  observacao: z.string().trim().max(500).optional(),
})
export type RespostaRegime = z.infer<typeof RespostaRegime>
